import { useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Users, UserCog, ShieldCheck, Lock, Eye, Award } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRIC_LABELS = {
  optimizations: "Optimizations", applications: "Applications", shortlisted: "Shortlisted (RTR)",
  interviews: "Interviews", offers: "Offers (Job won)",
};
const RANGES = [{ k: "week", l: "7d" }, { k: "month", l: "30d" }, { k: "all", l: "All time" }];

const chip = (active) => ({
  padding: "0.35rem 0.8rem", borderRadius: 999, fontSize: "0.8rem", cursor: "pointer",
  border: `1px solid ${active ? "#6366f1" : "var(--border-main)"}`,
  background: active ? "rgba(99,102,241,0.15)" : "transparent",
  color: active ? "#6366f1" : "var(--text-secondary)",
});

export const CandidateAgencyView = ({ data: initial }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(initial);
  const [range, setRange] = useState(initial?.stats?.range || "all");

  const reload = useCallback(async (rng) => {
    try {
      const r = await axios.get(`${API}/agencies/${initial.id}/my-view?range=${rng}`);
      setData(r.data);
    } catch { /* ignore */ }
  }, [initial.id]);

  const stats = data.stats;
  const sharedCount = data.my_share ? Object.values(data.my_share).filter(Boolean).length : null;

  return (
    <div className="page-container fade-in" data-testid="candidate-agency-view">
      <button className="btn-secondary" data-testid="agency-back-btn" onClick={() => navigate("/app/agencies")}><ArrowLeft size={16} /> Agencies</button>

      <div className="page-header" style={{ marginTop: "0.8rem" }}>
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Building2 size={22} /> {data.name}
            <span style={{ fontSize: "0.66rem", padding: "2px 8px", borderRadius: 999, background: "rgba(99,102,241,0.15)", color: "#6366f1", display: "inline-flex", alignItems: "center", gap: 3 }}><Eye size={11} /> Member</span>
          </h1>
          <p style={{ color: "var(--text-secondary)" }}>@{data.handle}{data.tagline ? ` · ${data.tagline}` : (data.description ? ` · ${data.description}` : "")}</p>
        </div>
      </div>

      {/* My team */}
      <div className="card" data-testid="candidate-my-team" style={{ padding: "1.2rem 1.4rem", marginBottom: "1.2rem" }}>
        <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><Users size={15} /> Your team</div>
        {data.my_team ? (
          <div>
            <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>{data.my_team.name}</div>
            {data.my_team.head_name && (
              <div style={{ fontSize: "0.84rem", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 5, marginTop: 3 }}>
                <UserCog size={14} /> Led by {data.my_team.head_name}{data.my_team.head_username ? ` (@${data.my_team.head_username})` : ""}
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: "var(--text-secondary)", fontSize: "0.88rem" }}>You haven't been assigned to a team yet.</div>
        )}
      </div>

      {/* Agency stats (gated by agency's member-visibility) */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: "0.8rem" }}>
        <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 6 }}><Building2 size={18} /> About {data.name}</h3>
        {stats && (
          <div style={{ display: "flex", gap: 6 }} data-testid="candidate-range-selector">
            {RANGES.map((r) => (
              <button key={r.k} data-testid={`candidate-range-${r.k}`} onClick={() => { setRange(r.k); reload(r.k); }} style={chip(range === r.k)}>{r.l}</button>
            ))}
          </div>
        )}
      </div>

      {!stats ? (
        <div className="card" data-testid="candidate-stats-private" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
          <Lock size={30} style={{ opacity: 0.4 }} />
          <p>This agency keeps its performance stats private to staff.</p>
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "0.8rem", marginBottom: "1rem" }}>
            {stats.member_count !== null && stats.member_count !== undefined && (
              <div className="card" data-testid="candidate-stat-members" style={{ padding: "1.1rem" }}>
                <div style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>Candidates in agency</div>
                <div style={{ fontSize: "1.8rem", fontWeight: 700, marginTop: 4 }}>{stats.member_count}</div>
              </div>
            )}
            {stats.candidates_placed !== null && stats.candidates_placed !== undefined && (
              <div className="card" data-testid="candidate-stat-placed" style={{ padding: "1.1rem", borderColor: "rgba(16,185,129,0.35)" }}>
                <div style={{ fontSize: "0.76rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4 }}><Award size={13} /> Candidates placed</div>
                <div style={{ fontSize: "1.8rem", fontWeight: 700, marginTop: 4, color: "#10b981" }}>{stats.candidates_placed}</div>
              </div>
            )}
            {(stats.visible_metrics || []).map((m) => (
              <div key={m} className="card" data-testid={`candidate-stat-${m}`} style={{ padding: "1.1rem" }}>
                <div style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>{METRIC_LABELS[m]}</div>
                <div style={{ fontSize: "1.8rem", fontWeight: 700, marginTop: 4 }}>{stats.totals?.[m] ?? 0}</div>
              </div>
            ))}
          </div>
          {(stats.visible_metrics || []).length === 0 && stats.member_count === null && stats.candidates_placed === null && (
            <div className="card" style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-secondary)" }}>This agency hasn't shared any aggregate stats with members.</div>
          )}
        </>
      )}

      {/* What you share */}
      {data.my_share && (
        <div className="card" data-testid="candidate-share-summary" style={{ padding: "1.1rem 1.4rem", marginTop: "1.2rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ShieldCheck size={18} style={{ color: sharedCount > 0 ? "#10b981" : "#ef4444" }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>You're sharing {sharedCount}/5 of your stats with this agency</div>
              <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>Control exactly what they see in your Privacy settings.</div>
            </div>
          </div>
          <button className="btn-secondary" data-testid="candidate-manage-privacy" onClick={() => navigate("/app/privacy")}>Manage sharing</button>
        </div>
      )}
    </div>
  );
};

export default CandidateAgencyView;
